import { useState, useCallback } from 'react';
import api from './api';

/**
 * Hook for calculating enrollment pricing using the backend API
 * Provides centralized, consistent pricing across the application
 */
export const usePriceCalculation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priceData, setPriceData] = useState(null);

  const calculatePrice = useCallback(async ({
    classIds,
    paymentPeriod = '6weeks',
    isProRated = false,
    weeksRemaining = 6,
    discountPercent = 0
  }) => {
    if (!classIds || classIds.length === 0) {
      setPriceData({
        subtotal: 0,
        discount_amount: 0,
        processing_fee: 0,
        total: 0,
        weeks_remaining: weeksRemaining,
        is_mid_term: false,
        class_breakdown: []
      });
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/payments/calculate-price', {
        class_ids: classIds,
        payment_period: paymentPeriod,
        is_pro_rated: isProRated,
        weeks_remaining: weeksRemaining,
        discount_percent: discountPercent
      });

      setPriceData(response.data);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to calculate price';
      setError(errorMsg);
      console.error('Price calculation error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Calculate price for multiple students with sibling discount
   */
  const calculateMultiStudentPrice = useCallback(async (students, paymentPeriod, isProRated, weeksRemaining) => {
    const results = [];
    let grandTotal = 0;
    let totalDiscount = 0;
    let siblingDiscount = 0;

    for (let i = 0; i < students.length; i++) {
      const student = students[i];
      const classIds = [
        ...(student.coreClass ? [student.coreClass.id] : []),
        ...student.addons.map(a => a.id)
      ];

      if (classIds.length === 0) continue;

      const priceResult = await calculatePrice({
        classIds,
        paymentPeriod,
        isProRated,
        weeksRemaining,
        discountPercent: 0
      });

      if (priceResult) {
        let studentTotal = priceResult.subtotal;
        let studentSiblingDiscount = 0;

        // Apply 30% sibling discount for 2nd+ students
        if (i > 0) {
          studentSiblingDiscount = studentTotal * 0.3;
          studentTotal = studentTotal * 0.7;
        }

        results.push({
          studentId: student.id,
          studentName: `${student.firstName} ${student.lastName}`,
          ...priceResult,
          siblingDiscount: studentSiblingDiscount,
          adjustedTotal: studentTotal
        });

        grandTotal += studentTotal;
        totalDiscount += priceResult.discount_amount + studentSiblingDiscount;
        siblingDiscount += studentSiblingDiscount;
      }
    }

    // Calculate processing fee on discounted total
    const processingFee = Math.round(grandTotal * 0.015 * 100) / 100;
    const finalTotal = Math.round((grandTotal + processingFee) * 100) / 100;

    return {
      students: results,
      subtotal: grandTotal,
      totalDiscount,
      siblingDiscount,
      processingFee,
      grandTotal: finalTotal,
      studentCount: results.length
    };
  }, [calculatePrice]);

  return {
    loading,
    error,
    priceData,
    calculatePrice,
    calculateMultiStudentPrice
  };
};

export default usePriceCalculation;
